import React from 'react';
import { Button } from '@/components/ui/button';
import ModalResponsive, { ModalResponsiveFooter } from '@/components/commons/modal-responsive';
import { Info, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

export interface ModalConfirmationAlertProps extends React.HtmlHTMLAttributes<HTMLElement> {
  openModal?: boolean;
  onConfirm?: () => void;
  setOpenModal: (open: boolean) => void;
  isLoadingConfirm: boolean;
  type: 'error' | 'warning' | 'info' | 'success';
  title?: string;
  description?: string;
}

const ICONS = {
  info: <Info width={60} height={60} className='text-blue-500' />,
  error: <XCircle width={60} height={60} className='text-red-500' />,
  warning: <AlertCircle width={60} height={60} className='text-yellow-500' />,
  success: <CheckCircle width={60} height={60} className='text-green-500' />,
};

export default function ModalConfirmationAlert({
  openModal,
  onConfirm,
  setOpenModal,
  isLoadingConfirm,
  type,
  title,
  description,
}: ModalConfirmationAlertProps) {
  const handleOnConfirm = () => {
    if (isLoadingConfirm) return;
    onConfirm?.();
  };

  const handleOnClose = () => {
    setOpenModal(false);
  };

  return (
    <ModalResponsive openModal={openModal} setOpenModal={handleOnClose} className='max-w-xl'>
      <div className='flex flex-col items-center justify-center px-6 py-6'>
        <div className='flex flex-col items-center'>
          <div className='mb-4'>{ICONS[type]}</div>
          <div className='text-center'>
            <div className='text-xl font-bold text-[#5464E]'>{title || 'Are you sure?'}</div>
            <div className='mt-2 text-sm text-[#8B8D97]'>
              {description || 'You cannot undo this action.'}
            </div>
          </div>
        </div>
      </div>
      <ModalResponsiveFooter>
        <div className='flex justify-between space-x-2'>
          <Button
            variant='secondary'
            onClick={() => setOpenModal(false)}
            disabled={isLoadingConfirm}
            data-testid='cancel-action-button'
          >
            Cancel
          </Button>
          <Button
            onClick={handleOnConfirm}
            data-testid='confirm-action-button'
            variant={type === 'error' ? 'destructive' : 'default'}
            className={
              type === 'warning'
                ? 'bg-[#DF4D4D] text-white hover:bg-[#DF4D4D]'
                : type === 'success'
                ? 'bg-[#00C48C] text-white hover:bg-[#00C48C]'
                : ''
            }
            disabled={isLoadingConfirm}
            isLoading={isLoadingConfirm}
          >
            Confirm
          </Button>
        </div>
      </ModalResponsiveFooter>
    </ModalResponsive>
  );
}
